import React from 'react'
import { Box, TextField, Select, MenuItem, FormControl, InputLabel } from '@mui/material'
import { useTranslation } from 'react-i18next'

export default function MetadataEditor({ metadata, onChange, docType, referenceData, hideFields = [] }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language

  // Get localized name from a reference data item's name dict (e.g. {en: "...", fr: "...", ar: "..."})
  const localizedName = (nameObj, fallback) => {
    if (!nameObj) return fallback || ''
    return nameObj[lang] || nameObj.en || nameObj.fr || fallback || ''
  }

  const update = (field, value) => {
    onChange({ ...metadata, [field]: value })
  }

  // Helper: update a nested object field (e.g. gazette_info.number)
  const updateNested = (parent, field, value) => {
    onChange({ ...metadata, [parent]: { ...(metadata[parent] || {}), [field]: value } })
  }

  // Helper: strip time portion from ISO datetime for date inputs
  const toDateValue = (val) => {
    if (!val) return ''
    return val.substring(0, 10)
  }

  // Resolve legislation_type: metadata may have legislation_type_id (UUID) instead of legislation_type_code
  const getLegislationTypeValue = () => {
    if (metadata.legislation_type_code) return metadata.legislation_type_code
    if (metadata.legislation_type_id && referenceData?.legislation_types) {
      const match = referenceData.legislation_types.find((lt) => lt.id === metadata.legislation_type_id)
      return match?.code || ''
    }
    return ''
  }

  // Resolve jurisdiction: metadata may have jurisdiction_id (UUID) instead of jurisdiction_code
  const getJurisdictionValue = () => {
    if (metadata.jurisdiction_code) return metadata.jurisdiction_code
    if (metadata.jurisdiction_id && referenceData?.jurisdictions) {
      const match = referenceData.jurisdictions.find((j) => j.id === metadata.jurisdiction_id)
      return match?.code || ''
    }
    return ''
  }

  // Court level options with translations
  const courtLevels = [
    { value: 'supreme', label: t('du_court_supreme') },
    { value: 'cassation', label: t('du_court_cassation') },
    { value: 'appeal', label: t('du_court_appeal') },
    { value: 'first_instance', label: t('du_court_first_instance') },
    { value: 'proximity', label: t('du_court_proximity') },
  ]

  // Court division options with translations
  const courtDivisions = [
    { value: 'civil', label: t('du_division_civil') },
    { value: 'commercial', label: t('du_division_commercial') },
    { value: 'criminal', label: t('du_division_criminal') },
    { value: 'administrative', label: t('du_division_administrative') },
    { value: 'labor', label: t('du_division_labor') },
    { value: 'family', label: t('du_division_family') },
  ]

  // Decision type options with translations
  const decisionTypes = [
    { value: 'accepted', label: t('du_decision_accepted') },
    { value: 'rejected', label: t('du_decision_rejected') },
    { value: 'partially_accepted', label: t('du_decision_partially_accepted') },
    { value: 'dismissed', label: t('du_decision_dismissed') },
  ]

  // Authority type options with translations
  const authorityTypes = [
    { value: 'parliament', label: t('du_authority_parliament') },
    { value: 'king', label: t('du_authority_king') },
    { value: 'president', label: t('du_authority_president') },
    { value: 'cabinet', label: t('du_authority_cabinet') },
    { value: 'ministry', label: t('du_authority_ministry') },
  ]

  if (docType === 'case_law') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField label={t('du_case_number')} size="small" fullWidth
          value={metadata.case_number || ''} onChange={(e) => update('case_number', e.target.value)} />
        <TextField label={t('du_decision_number')} size="small" fullWidth
          value={metadata.decision_number || ''} onChange={(e) => update('decision_number', e.target.value)} />
        <TextField label={t('du_dossier_number')} size="small" fullWidth
          value={metadata.dossier_number || ''} onChange={(e) => update('dossier_number', e.target.value)} />
        <TextField label={t('du_case_year')} size="small" type="number" fullWidth
          value={metadata.case_year || ''} onChange={(e) => update('case_year', parseInt(e.target.value) || null)} />
        <TextField label={t('du_judgment_date')} size="small" type="date" fullWidth InputLabelProps={{ shrink: true }}
          value={toDateValue(metadata.judgment_date)} onChange={(e) => update('judgment_date', e.target.value)} />
        <TextField label={t('du_court_name')} size="small" fullWidth
          value={metadata.court_name || ''} onChange={(e) => update('court_name', e.target.value)} />
        <FormControl size="small" fullWidth>
          <InputLabel>{t('du_court_level')}</InputLabel>
          <Select value={metadata.court_level || ''} label={t('du_court_level')}
            onChange={(e) => update('court_level', e.target.value)}>
            {courtLevels.map((l) => (
              <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" fullWidth>
          <InputLabel>{t('du_court_division')}</InputLabel>
          <Select value={metadata.court_division || ''} label={t('du_court_division')}
            onChange={(e) => update('court_division', e.target.value)}>
            {courtDivisions.map((d) => (
              <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField label={t('du_case_type')} size="small" fullWidth
          value={metadata.case_type || ''} onChange={(e) => update('case_type', e.target.value)} />
        <TextField label={t('du_subject_matter')} size="small" fullWidth multiline rows={2}
          value={metadata.subject_matter || ''} onChange={(e) => update('subject_matter', e.target.value)} />
        <FormControl size="small" fullWidth>
          <InputLabel>{t('du_decision_type')}</InputLabel>
          <Select value={metadata.decision_type || ''} label={t('du_decision_type')}
            onChange={(e) => update('decision_type', e.target.value)}>
            {decisionTypes.map((d) => (
              <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" fullWidth>
          <InputLabel>{t('du_legal_domain')}</InputLabel>
          <Select value={metadata.legal_domain || ''} label={t('du_legal_domain')}
            onChange={(e) => update('legal_domain', e.target.value)}>
            {(referenceData?.legal_domains || []).map((d) => (
              <MenuItem key={d.code} value={d.code}>{localizedName(d.name, d.code)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField label={t('du_source_url') || 'Source URL'} size="small" fullWidth
          value={metadata.source_url || ''} onChange={(e) => update('source_url', e.target.value)}
          placeholder="https://..." />
      </Box>
    )
  }

  // Legislation fields
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField label={t('du_titre')} size="small" fullWidth
        value={metadata.title || ''} onChange={(e) => update('title', e.target.value)} />
      <TextField label={t('du_document_number')} size="small" fullWidth
        value={metadata.doc_number || ''} onChange={(e) => update('doc_number', e.target.value)} />
      <TextField label={t('du_issue_year')} size="small" type="number" fullWidth
        value={metadata.issue_year || ''} onChange={(e) => update('issue_year', parseInt(e.target.value) || null)} />
      <TextField label={t('du_issue_date')} size="small" type="date" fullWidth InputLabelProps={{ shrink: true }}
        value={toDateValue(metadata.issue_date)} onChange={(e) => update('issue_date', e.target.value)} />
      <TextField label={t('du_publication_date_field')} size="small" type="date" fullWidth InputLabelProps={{ shrink: true }}
        value={toDateValue(metadata.publication_date)} onChange={(e) => update('publication_date', e.target.value)} />
      <TextField label={t('du_effective_date')} size="small" type="date" fullWidth InputLabelProps={{ shrink: true }}
        value={toDateValue(metadata.effective_date)} onChange={(e) => update('effective_date', e.target.value)} />
      <TextField label={t('du_last_update')} size="small" type="date" fullWidth InputLabelProps={{ shrink: true }}
        value={toDateValue(metadata.last_update)} onChange={(e) => update('last_update', e.target.value)} />

      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField label={t('du_gazette_name')} size="small" sx={{ flex: 2 }}
          value={metadata.gazette_info?.name || ''} onChange={(e) => updateNested('gazette_info', 'name', e.target.value)} />
        <TextField label={t('du_gazette_number')} size="small" sx={{ flex: 1 }}
          value={metadata.gazette_info?.number || ''} onChange={(e) => updateNested('gazette_info', 'number', e.target.value)} />
        <TextField label={t('du_gazette_date')} size="small" type="date" sx={{ flex: 1 }}
          InputLabelProps={{ shrink: true }}
          value={toDateValue(metadata.gazette_info?.date)} onChange={(e) => updateNested('gazette_info', 'date', e.target.value)} />
      </Box>

      <FormControl size="small" fullWidth>
        <InputLabel>{t('du_legislation_type')}</InputLabel>
        <Select value={getLegislationTypeValue()} label={t('du_legislation_type')}
          onChange={(e) => update('legislation_type_code', e.target.value)}>
          {(referenceData?.legislation_types || []).map((lt) => (
            <MenuItem key={lt.code} value={lt.code}>{localizedName(lt.name, lt.code)}</MenuItem>
          ))}
        </Select>
      </FormControl>
      {!hideFields.includes('jurisdiction') && (
        <FormControl size="small" fullWidth>
          <InputLabel>{t('du_jurisdiction')}</InputLabel>
          <Select value={getJurisdictionValue()} label={t('du_jurisdiction')}
            onChange={(e) => update('jurisdiction_code', e.target.value)}>
            {(referenceData?.jurisdictions || []).map((j) => (
              <MenuItem key={j.code} value={j.code}>{localizedName(j.name, j.code)}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <FormControl size="small" fullWidth>
        <InputLabel>{t('du_authority_type')}</InputLabel>
        <Select value={metadata.authority_type || ''} label={t('du_authority_type')}
          onChange={(e) => update('authority_type', e.target.value)}>
          {authorityTypes.map((a) => (
            <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField label={t('du_authority_name')} size="small" fullWidth
        value={metadata.authority_name || ''} onChange={(e) => update('authority_name', e.target.value)} />
      <FormControl size="small" fullWidth>
        <InputLabel>{t('du_legal_domain')}</InputLabel>
        <Select value={metadata.legal_domain || ''} label={t('du_legal_domain')}
          onChange={(e) => update('legal_domain', e.target.value)}>
          {(referenceData?.legal_domains || []).map((d) => (
            <MenuItem key={d.code} value={d.code}>{localizedName(d.name, d.code)}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField label={t('du_hijri_date')} size="small" fullWidth
        value={metadata.date_hijri || ''} onChange={(e) => update('date_hijri', e.target.value)} />
      <TextField label={t('du_source_url') || 'Source URL'} size="small" fullWidth
        value={metadata.source_url || ''} onChange={(e) => update('source_url', e.target.value)}
        placeholder="https://..." />
    </Box>
  )
}
